"use client";
import { SearchIcon } from "lucide-react";
import { cn } from "cy-fe/src/utils/cn";
import { ComboBox, ComboOption } from "cy-ui/components/Combobox";
import {
  AddressSearchRequest,
  SearchSuggesetion,
  SearchSuggestionResponse,
} from "cy-api/aws/location/models";
import { useQuery } from "@tanstack/react-query";
import { ErrorWithData } from "cy-util/src/error/ErrorWithData";
import { useState } from "react";
import { useDebounce } from "use-debounce";
import { Combobox, ComboboxOption } from "@headlessui/react";
import { LoadingSpinner } from "cy-ui/components/loaders/LoadingSpinner";
import { Country } from "./models";

export function AddressFinder({
  onChange,
  country,
}: {
  country: Country;
  onChange: (suggestion: SearchSuggesetion | null) => void;
}) {
  const [apiQuery, setApiQuery] = useState("");
  const [value, setValue] = useState<SearchSuggesetion | null>(null);
  const [debouncedApiQuery] = useDebounce(apiQuery, 200);
  const suggestionQuery = useQuery<SearchSuggestionResponse>(
    [debouncedApiQuery],
    async () => {
      const req: AddressSearchRequest = {
        MaxResults: 15,
        FilterCountries: country.countryCode3
          ? [country.countryCode3]
          : undefined,
        Text: debouncedApiQuery,
        FilterCategories: ["AddressType"],
      };
      const resp = await fetch("/api/public/address-finder", {
        method: "POST",
        body: JSON.stringify(req),
      });
      if (!resp.ok) {
        throw new ErrorWithData("Failed to call address finder", resp);
      }
      return await resp.json();
    },
    { enabled: !!debouncedApiQuery },
  );

  function onChangeHandler(suggestion: SearchSuggesetion | null) {
    setValue(suggestion);
    onChange(suggestion);
  }
  return (
    <ComboBox<SearchSuggesetion>
      buttonChildren={() => (
        <>
          <SearchIcon className="text-gray-400" />
          <div className="font-semibold text-gray-400">
            Find Your {country.name} Address
          </div>
        </>
      )}
      buttonClassName={cn("bg-gray-50 rounded border-none flex-grow")}
      getOptions={(query) => {
        setApiQuery(query);
        query = query.trim().toLowerCase();
        return [];
      }}
      maxHeight={16 * 30}
      onChange={onChangeHandler}
      renderItems={() => {
        const items = suggestionQuery.data?.Results || [];
        if (suggestionQuery.isFetching) {
          return (
            <div className="py-20">
              <LoadingSpinner className="mx-auto" />
            </div>
          );
        }
        return items.map((x) => (
          <ComboboxOption value={x} key={x.PlaceId}>
            {({ focus }) => (
              <ComboOption active={focus} selected={false}>
                <div>{x.Text}</div>
              </ComboOption>
            )}
          </ComboboxOption>
        ));
      }}
      value={value}
    />
  );
}
